import React, { useEffect, useState } from "react";
import {
  Card,
  Col,
  Collapse,
  Divider,
  Dropdown,
  Row,
  Statistic,
  Tag,
  Typography,
  Spin,
} from "antd";
import {
  SafetyOutlined,
  EditOutlined,
  SettingOutlined,
  DislikeOutlined,
} from "@ant-design/icons";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import {
  apiConfig,
  labelConfig,
  staticConfig,
} from "./financialInstrument.config";
import { BOM } from "../../../services/definedMethod";
import cnt from "../../../services/definedConstant";

const { Panel } = Collapse;
const { Text, Title } = Typography;

const ViewSecurityInstrument = ({ userData }) => {
  const { session } = userData;
  const { state: instrumentId } = useLocation();

  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);

  /* ---------------- HELPERS ---------------- */

  const formatDateArray = (arr) => {
    if (!Array.isArray(arr) || arr.length !== 3) return "-";
    const [year, month, day] = arr;
    return new Date(year, month - 1, day).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const resolveDisplayValue = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    if (typeof val === "object") {
      if (val.text) return val.text;
      if (val.value !== undefined) return val.value;
      return "-";
    }
    return val;
  };

  const renderTag = (value) => {
    if (value === "Yes" || value === true) {
      return (
        <Tag
          style={{
            background: "#ECFDF3",
            border: "1px solid #ABEFC6",
            borderRadius: 16,
          }}
        >
          <Text style={{ color: "#067647", fontWeight: 600 }}>Yes</Text>
        </Tag>
      );
    }

    if (value === "No" || value === false) {
      return (
        <Tag
          style={{
            background: "#F9FAFB",
            border: "1px solid #EAECF0",
            borderRadius: 16,
          }}
        >
          <Text style={{ color: "#344054", fontWeight: 600 }}>No</Text>
        </Tag>
      );
    }

    if (value === "-") {
      return <Text style={{ color: "#6b7280" }}>-</Text>;
    }

    return (
      <Tag
        style={{
          background: "#FDF4FF",
          border: "1px solid #F6D0FE",
          borderRadius: 16,
        }}
      >
        <Text style={{ color: "#9F1AB1", fontWeight: 600 }}>
          {String(value)}
        </Text>
      </Tag>
    );
  };

  const renderRow = (item, index, sectionIndex, sections) => {
    const isLast = index === sections[sectionIndex].items.length - 1;

    return (
      <div
        key={item.name}
        style={{
          display: "flex",
          borderBottom: isLast ? "none" : "1px solid #f0f0f0",
        }}
      >
        <div
          style={{
            padding: "16px 24px",
            width: "50%",
            backgroundColor: "#f9f9f9",
            fontWeight: 500,
            borderRight: "1px solid #f0f0f0",
          }}
        >
          {item.name}
        </div>

        <div
          style={{
            padding: "16px 24px",
            width: "50%",
            backgroundColor: "#fff",
          }}
        >
          {item.isTag ? (
            renderTag(resolveDisplayValue(item.value))
          ) : (
            <Text style={{ fontWeight: 600 }}>
              {resolveDisplayValue(item.value)}
            </Text>
          )}
        </div>
      </div>
    );
  };

  /* ---------------- API ---------------- */

  const getInstrumentById = (id) => {
    setLoading(true);
    const url = `${apiConfig.GetSecurityInstrument}/${id}`;
    const body = { session };

    BOM.FetchReqAction(body, url, (err, res) => {
      setLoading(false);
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
        return;
      }
      setDetails(res);
    });
  };

  useEffect(() => {
    if (instrumentId) getInstrumentById(instrumentId);
  }, [instrumentId]);

  /* ---------------- DATA SECTIONS ---------------- */

  const sections = details
    ? [
        {
          section: "Instrument Identification",
          items: [
            {
              name: "Instrument Name",
              value: details.instrumentName,
              isTag: true,
            },
            { name: "Short Name", value: details.shortName },
            { name: "ISIN", value: details.isin },
            // { name: "CUSIP", value: details.cusip },
            { name: "Local Code/ Market Code", value: details.internalCode },
          ],
        },
        {
          section: "Classification",
          items: [
            {
              name: "Instrument Type",
              value: details.instrumentType,
              isTag: true,
            },
            {
              name: "Instrument Category",
              value: details.instrumentCategory,
              isTag: true,
            },
            { name: "Asset Class", value: details.assetClass, isTag: true },
            { name: "Currency", value: details.currencyCode, isTag: true },
            { name: "Country of Issuer", value: details.country?.name },
            { name: "Issuer Name", value: details.issueName },
            { name: "Issuer Type", value: details.issueType?.text },
          ],
        },
        {
          section: "Instrument Structure / Terms",
          items: [
            {
              name: "Face Value",
              value: details.faceValueDenomination
                ? Number(details.faceValueDenomination).toLocaleString()
                : "-",
            },
            { name: "Issue Date", value: formatDateArray(details.issueDate) },
            {
              name: "Maturity Date",
              value: formatDateArray(details.maturityDate),
            },
            {
              name: "Day Count Convention",
              value: details.dayCountConvention,
              isTag: true,
            },
            {
              name: "Business Day Convention",
              value: details.businessDayConvention,
              isTag: true,
            },
            { name: "Holiday Calendar", value: details.holidayCalendar },
            { name: "Coupon Rate (%)", value: details.couponRate },
            {
              name: "Coupon Frequency",
              value: details.couponFrequency,
              isTag: true,
            },
            { name: "Coupon Basis", value: details.couponBasis, isTag: true },
            { name: "Reset Index", value: details.resetIndex },
          ],
        },
        {
          section: "Pricing Information",
          items: [
            {
              name: "Quotation Type",
              value: details.quotationType,
              isTag: true,
            },
            { name: "Market Type", value: details.marketType, isTag: true },
            { name: "Decimal Place", value: details.ticketSize },
            {
              name: "Valuation Approach",
              value: details.valuationApproach,
              isTag: true,
            },
            { name: "Linked Product", value: details.linkedProductName },
          ],
        },
      ]
    : [];

  const actionMenu = {
    items: [
      {
        key: "1",
        label: "Modify Instrument",
        icon: <EditOutlined />,
        disabled: true,
      },
      { key: "2", label: "Approve", icon: <SafetyOutlined />, disabled: true },
      { key: "3", label: "Reject", icon: <DislikeOutlined />, disabled: true },
    ],
  };

  /* ---------------- UI ---------------- */

  return (
    <MainLayout pageItems={staticConfig.viewSecurityInstrument}>
      <TopContent
        pageTitle={labelConfig.securityInstrument}
        createPageTitle={labelConfig.viewSecurityInstrument}
      />

      <Card
        headStyle={{ borderBottomWidth: 0 }}
        style={{
          boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
          borderRadius: 20,
          marginTop: 20,
        }}
      >
        <Spin spinning={loading}>
          <div style={{ paddingTop: 20, marginBottom: 50 }}>
            <Title level={4}>{labelConfig.generalInfo}</Title>
            <Divider />

            <Row>
              <Col span={12}>
                {/* <Dropdown.Button menu={actionMenu} icon={<SettingOutlined />}>
                  Actions
                </Dropdown.Button> */}
              </Col>

              <Col span={12}>
                <Card hoverable style={{ backgroundColor: "#EAE7EC" }}>
                  <Row>
                    <Col span={12}>
                      <Statistic
                        title="Instrument Name"
                        value={details?.instrumentName || "-"}
                      />
                      <Statistic
                        title="Short Name"
                        value={details?.shortName || "-"}
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>

            <Row
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 20,
                marginTop: 20,
              }}
            >
              {sections.map((section, sectionIndex) => (
                <Col key={section.section}>
                  <Collapse
                    className="custom-collapse"
                    expandIcon={<ChevronDown />}
                    style={{ backgroundColor: "transparent" }}
                  >
                    <Panel
                      header={
                        <span style={{ fontSize: 16, fontWeight: 500 }}>
                          {section.section}
                        </span>
                      }
                    >
                      <div
                        style={{
                          border: "1px solid #f0f0f0",
                          borderRadius: 8,
                          overflow: "hidden",
                        }}
                      >
                        {section.items.map((item, itemIndex) =>
                          renderRow(item, itemIndex, sectionIndex, sections)
                        )}
                      </div>
                    </Panel>
                  </Collapse>
                </Col>
              ))}
            </Row>
          </div>
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps)(ViewSecurityInstrument);
